import { useState, useRef, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { formatPercentage } from '@/lib/utils/stringUtils'
import type { Experiment, ExperimentVariant } from '@/hooks/useExperiments'

// Import assets
import HumblyticsLogo from '@/assets/humblytics-web-app-logo.svg'
import Bars3DImage from '@/assets/images/3d-bars.png'

interface ShareExperimentModalProps {
  experiment: Experiment | null
  variants: ExperimentVariant[]
  winner: ExperimentVariant | null
  significance: number
  open: boolean
  onOpenChange: (open: boolean) => void
}
import { HugeiconsIcon } from "@hugeicons/react"
import { FileDownloadIcon, AnalyticsDownIcon, AnalyticsUpIcon } from "@hugeicons/core-free-icons"
import { getVariantColor } from '@/lib/utils/chartColors'

export default function ShareExperimentModal({
  experiment,
  variants,
  winner,
  significance,
  open,
  onOpenChange,
}: ShareExperimentModalProps) {
  const shareCardRef = useRef<HTMLDivElement>(null)
  const [isGenerating, setIsGenerating] = useState(false)

  const control = variants.find(v => v.isControl)
  
  const getImprovement = useCallback(() => {
    if (!winner || !control || winner.isControl) return 0
    const controlRate = control.conversionRate || 0
    const winnerRate = winner.conversionRate || 0
    if (controlRate === 0) return winnerRate > 0 ? 100 : 0
    return ((winnerRate - controlRate) / controlRate) * 100
  }, [winner, control])

  const improvement = getImprovement()

  const totalViews = variants.reduce((sum, v) => sum + (v.sessions || 0), 0)

  const getTestGoalLabel = () => {
    if (!experiment?.testGoal) return 'Increase CTR'
    const goal = experiment.testGoal
    switch (goal) {
      case 'Form Submission':
      case 'form_submissions':
        return 'Increase Forms'
      case 'Click Through Rate':
      case 'click_through_rate':
        return 'Increase CTR'
      case 'Page Funnel':
      case 'page_funnel':
        return 'Reach Destination'
      case 'Session Time':
      case 'session_time':
        return 'Increase Time'
      case 'Bounce Rate':
      case 'bounce_rate':
        return 'Reduce Bounces'
      case 'revenue':
        return 'Increase Revenue'
      default:
        return 'Increase CTR'
    }
  }

  const getWinnerLabel = () => {
    if (!winner) return 'TIED'
    return winner.isControl ? 'CONTROL' : `VARIANT ${winner.id}`
  }

  const generateImage = async () => {
    if (!shareCardRef.current || isGenerating) return

    setIsGenerating(true)
    try {
      // Dynamically import html-to-image
      const { toPng } = await import('html-to-image')
      
      // Wait for fonts to load
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready
      }
      await new Promise((resolve) => setTimeout(resolve, 200))

      const dataUrl = await toPng(shareCardRef.current, {
        backgroundColor: '#ffffff',
        pixelRatio: 2,
        quality: 1.0,
        cacheBust: true,
      })

      const link = document.createElement('a')
      const sanitizedName = (experiment?.experimentName || 'split-test-results')
        .replace(/[^a-z0-9]/gi, '-')
        .toLowerCase()
      link.download = `${sanitizedName}-${Date.now()}.png`
      link.href = dataUrl
      link.click()

      toast.success('Image downloaded successfully!')
    } catch (error) {
      console.error('Failed to generate image:', error)
      toast.error('Failed to generate image. Install html-to-image package for this feature.')
    } finally {
      setIsGenerating(false)
    }
  }

  const shareToTwitter = async () => {
    const text = encodeURIComponent(
      `Check out my split test results! ${experiment?.experimentName || 'Split Test'} - ${getTestGoalLabel()}`
    )
    const url = encodeURIComponent(window.location.href)
    window.open(
      `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      '_blank'
    )
    await generateImage()
    toast.success('Image downloaded! You can now attach it to your tweet.')
  }

  const shareToLinkedIn = async () => {
    const url = encodeURIComponent(window.location.href)
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      '_blank'
    )
    await generateImage()
    toast.success('Image downloaded! You can now attach it to your LinkedIn post.')
  }

  if (!experiment) return null

  const maxCR = Math.max(...variants.map(v => v.conversionRate || 0), 1)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Share split test results</DialogTitle>
          <DialogDescription>
            Generate a shareable image of your split test results
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Share Card Preview */}
          <div
            ref={shareCardRef}
            className="relative w-full bg-card rounded-2xl p-4 border border-border shadow-sm"
            style={{ aspectRatio: '16/9', minHeight: '400px', maxHeight: '500px' }}
          >
            {/* Bento Grid Layout */}
            <div className="flex flex-col gap-2.5 h-full min-h-0">
              {/* Header */}
              <div className="flex items-center justify-between pb-2.5 border-b border-border/50 flex-shrink-0">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                    <img src={HumblyticsLogo} alt="Humblytics" className="w-4 h-4" />
                  </div>
                  <div>
                    <h1 className="text-sm font-semibold text-foreground leading-tight truncate max-w-md">
                      {experiment.experimentName}
                    </h1>
                    <p className="text-xs text-muted-foreground mt-0.5">{getTestGoalLabel()}</p>
                  </div>
                </div>
                <Badge variant="secondary" className="text-xs px-2.5 py-1 font-semibold bg-background border border-border flex-shrink-0">
                  SPLIT TEST BY HUMBLYTICS
                </Badge>
              </div>

              {/* Main Content Area */}
              <div className="flex-1 grid grid-cols-5 gap-2.5">
                {/* Combined Image and Leading Variant Card */}
                <div className="col-span-3 bg-card rounded-xl p-4 flex gap-4 border border-border/30">
                  <div className="flex-shrink-0 w-32 h-32 flex items-center justify-center">
                    <img
                      src={Bars3DImage}
                      alt="Split test variants visualization"
                      className="w-full h-full rounded-xl object-contain"
                    />
                  </div>

                  <div className="flex-1 flex flex-col justify-between min-w-0">
                    <div className="space-y-2.5">
                      <p className="text-xs text-muted-foreground">
                        Leading variant
                      </p>
                      {totalViews === 0 ? (
                        <Badge variant="outline" className="text-xs px-2.5 py-1 font-semibold">
                          NO DATA
                        </Badge>
                      ) : (
                        <Badge
                          variant="secondary"
                          className={cn(
                            'text-xs px-3 py-1.5 font-semibold',
                            winner && !winner.isControl && 'bg-primary-tint text-primary-strong ring-1 ring-primary-tint-ring',
                            winner?.isControl && 'bg-muted text-muted-foreground border border-border/40'
                          )}
                        >
                          {getWinnerLabel()}
                        </Badge>
                      )}
                    </div>
                    <div className="space-y-1">
                      {totalViews === 0 ? (
                        <div className="space-y-1">
                          <span className="text-2xl font-semibold text-muted-foreground">--</span>
                          <p className="text-xs text-muted-foreground">Waiting for data</p>
                        </div>
                      ) : significance >= 95 && improvement !== 0 && winner && !winner.isControl ? (
                        <>
                          <div className="flex items-baseline gap-2">
                            <span className={cn(
                              "text-4xl font-semibold leading-none",
                              improvement > 0 ? "text-primary-strong" : "text-foreground"
                            )}>
                              {improvement > 0 ? '+' : ''}{formatPercentage(improvement)}
                            </span>
                            {improvement > 0 ? (
                              <HugeiconsIcon icon={AnalyticsUpIcon} size={16} className="text-success flex-shrink-0 mt-1" />
                            ) : (
                              <HugeiconsIcon icon={AnalyticsDownIcon} size={16} className="text-danger flex-shrink-0 mt-1" />
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-2">
                            Winner confirmed - Improvement over Control
                          </p>
                        </>
                      ) : (
                        <div className="space-y-1">
                          <span className="text-3xl font-semibold text-foreground leading-none">
                            {winner ? formatPercentage(winner.conversionRate || 0) : '--'}
                          </span>
                          <p className="text-xs text-muted-foreground mt-2">
                            {significance < 95 ? `${formatPercentage(significance)} confidence - Gathering more data` : 'Conversion Rate'}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right Column - Stacked cards */}
                <div className="col-span-2 grid grid-rows-3 gap-1.5 min-h-0">
                  {/* Confidence Level */}
                  <div className="bg-card rounded-xl p-2.5 flex flex-col justify-center border border-border/30">
                    <p className="text-xs text-muted-foreground mb-1">
                      Confidence level
                    </p>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant={significance >= 95 ? 'default' : significance >= 80 ? 'secondary' : 'outline'}
                        className="text-xs px-2 py-0.5 font-semibold w-fit"
                      >
                        {formatPercentage(significance)}
                      </Badge>
                      <p className="text-xs text-muted-foreground leading-tight line-clamp-1 flex-1">
                        {significance >= 95 ? 'Statistically significant' : significance >= 80 ? 'Promising' : 'Not yet significant'}
                      </p>
                    </div>
                  </div>

                  {/* Total Views */}
                  <div className="bg-card rounded-xl p-2.5 flex flex-col justify-center border border-border/30">
                    <p className="text-xs text-muted-foreground mb-1">
                      Total views
                    </p>
                    <p className="text-2xl font-semibold text-foreground">
                      {totalViews.toLocaleString()}
                    </p>
                  </div>

                  {/* Variant Breakdown */}
                  <div className="bg-card rounded-xl p-2.5 flex flex-col justify-center border border-border/30">
                    <p className="text-xs text-muted-foreground mb-1.5">
                      Variants
                    </p>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                      {variants.map((variant, index) => (
                        <div key={variant.id} className="flex items-center gap-1.5">
                          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: getVariantColor(index, variant.isControl) }} />
                          <span className="text-xs font-medium text-foreground">
                            {variant.id}: {(variant.sessions || 0).toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Performance Results - Full width bottom card */}
              <div className="bg-card rounded-xl p-3 flex-shrink-0 border border-border/30">
                <p className="text-xs text-muted-foreground mb-2.5">
                  Performance results
                </p>
                {totalViews === 0 ? (
                  <div className="flex items-center justify-center h-16">
                    <p className="text-xs text-muted-foreground">No data available yet</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-4 mb-2.5">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Control</p>
                        <p className="text-lg font-semibold text-foreground">
                          {formatPercentage(control?.conversionRate || 0)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Best variant</p>
                        <p className="text-lg font-semibold text-foreground">
                          {winner ? formatPercentage(winner.conversionRate || 0) : '--'}
                        </p>
                      </div>
                    </div>
                    {/* Bar Chart Visualization */}
                    <div className="space-y-1.5 pt-2">
                      {variants.map((variant, index) => (
                        <div key={variant.id} className="flex items-center gap-2">
                          <div className="w-8 flex items-center gap-1 flex-shrink-0">
                            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: getVariantColor(index, variant.isControl) }} />
                            <span className="text-xs font-medium">{variant.id}</span>
                          </div>
                          <div className="flex-1 relative h-3 rounded bg-muted/40 overflow-hidden">
                            <div
                              className="h-full rounded transition-all"
                              style={{ width: `${maxCR > 0 ? ((variant.conversionRate || 0) / maxCR) * 100 : 0}%`, backgroundColor: getVariantColor(index, variant.isControl) }}
                            />
                          </div>
                          <span className="text-xs font-semibold w-10 text-right flex-shrink-0">
                            {formatPercentage(variant.conversionRate || 0)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={shareToTwitter}
                disabled={isGenerating}
                className="gap-2"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
                Share on Twitter
              </Button>
              <Button
                variant="outline"
                onClick={shareToLinkedIn}
                disabled={isGenerating}
                className="gap-2"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
                </svg>
                Share on LinkedIn
              </Button>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={generateImage} disabled={isGenerating} className="gap-2">
                {isGenerating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <HugeiconsIcon icon={FileDownloadIcon} size={16} />
                    Download image
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

